import {S} from './strings.mjs';
import {followStep, centerZoom, keepsFollow} from './follow.mjs';
const QUICK = {enableHighAccuracy: false, maximumAge: 120000, timeout: 5000};
const PRECISE = {enableHighAccuracy: true, maximumAge: 5000, timeout: 20000};
const FRESH_MS = 60000, NO_FIX_MS = 15000;

// Position button as follow toggle. Explicit MCP GPS mode may start 'follow'; normal loads use null.
// 'center' is an optional one-jump mode, never inferred from browser permission.
// firstZoom(coords): optional zoom for the first centring (search results: near level, but out far enough).
export function geolocate(map, maplibre, button, message, {start = null, onChange = () => {}, firstZoom = null, camera = options => options} = {}) {
  let state = 'off', last = null, marker, watch, timer, zooming = false, jumping = false, jumpTimer;
  const geo = () => navigator.geolocation;
  const render = () => {
    const busy = state === 'locating' || state === 'centering';
    button.dataset.follow = state === 'centering' ? 'off' : state;
    button.classList.toggle('locating', busy);
    button.setAttribute('aria-busy', String(busy));
    button.setAttribute('aria-pressed', String(state === 'on' || state === 'locating'));
    button.setAttribute('aria-label', busy ? S.locating : state === 'on' ? S.followStop : S.locate);
  };
  const stop = () => {
    clearTimeout(timer); timer = undefined;
    if (watch !== undefined) geo()?.clearWatch(watch);
    watch = undefined;
  };
  const showMarker = c => {
    if (!marker) {
      const dot = document.createElement('div'); dot.className = 'position'; dot.setAttribute('aria-label', S.myPosition);
      const arrow = document.createElement('div'); arrow.className = 'heading'; dot.append(arrow);
      marker = new maplibre.Marker({element: dot}).setLngLat([c.longitude, c.latitude]).addTo(map);
    }
    marker.setLngLat([c.longitude, c.latitude]);
    const arrow = marker.getElement().firstChild;
    arrow.hidden = !Number.isFinite(c.heading); arrow.style.transform = `rotate(${(c.heading || 0) - map.getBearing()}deg)`;
  };
  function run(event, position) {
    const before = state, step = followStep(state, event);
    state = step.state;
    for (const effect of step.effects) {
      const c = position?.coords;
      if (effect === 'watch') startWatch();
      else if (effect === 'stop') stop();
      else if (effect === 'marker' && c) showMarker(c);
      // Programmatic moves carry no originalEvent, so they never count as user interaction.
      // Zoom in to the near level (never out). While this jump runs, follow steps wait: a second fix right
      // after the first (cached fix, quick fix, precise watch) used to cut the zoom short far out (v12 bug).
      else if (effect === 'center' && c) {
        const custom = firstZoom ? firstZoom(c) : null;
        firstZoom = null;
        jumping = true;
        // Safety net: should no moveend come (nothing to animate), following resumes anyway.
        clearTimeout(jumpTimer); jumpTimer = setTimeout(() => { jumping = false; }, 1500);
        map.easeTo(camera({center: [c.longitude, c.latitude], zoom: custom ?? centerZoom(map.getZoom()), duration: 700}));
      }
      // A follow step would cut a running zoom short; moveend recentres instead.
      else if (effect === 'follow' && c && !zooming && !jumping) map.easeTo(camera({center: [c.longitude, c.latitude], duration: 600, easing: t => t * (2 - t)}));
      else if (effect === 'message') message(position);
    }
    render();
    if (before !== state) onChange(state);
  }
  const fix = position => {
    if (!position?.coords) return;
    const better = !last || state === 'on' || position.timestamp > last.timestamp || position.coords.accuracy < last.coords.accuracy;
    if (better) last = position;
    if (state !== 'off') run('fix', last);
  };
  const failure = error => {
    if (state === 'off') return;
    if (error?.code === 1) run('error', S.positionDenied);
    else if (error?.code !== 3 || !last) run('error', S.positionUnavailable);
  };
  function startWatch() {
    stop();
    const g = geo();
    if (!g) { queueMicrotask(() => run('error', S.noPosition)); return; }
    // The cached fix is good enough for the first jump; the precise watch refines it.
    if (last && Date.now() - last.timestamp < FRESH_MS) queueMicrotask(() => fix(last));
    g.getCurrentPosition(fix, e => { if (e?.code === 1) failure(e); }, QUICK);
    watch = g.watchPosition(fix, e => { if (e?.code === 1 || state !== 'on') failure(e); }, PRECISE);
    timer = setTimeout(() => { if (state === 'locating' || state === 'centering') failure({code: 2}); }, NO_FIX_MS);
  }
  // Any pan, pinch, rotate, pitch or pointer-anchored zoom by the user ends follow mode;
  // zoom buttons and +/- keys keep it (see keepsFollow).
  for (const type of ['dragstart', 'zoomstart', 'rotatestart', 'pitchstart', 'movestart']) {
    map.on(type, event => {
      const original = event?.originalEvent;
      if (!original || state === 'off') return;
      if (keepsFollow(original)) { if (type === 'zoomstart' || type === 'movestart') zooming = true; return; }
      zooming = false; run('interaction');
    });
  }
  // After a kept zoom, put the position back in the centre (a follow step may have been interrupted).
  map.on('moveend', () => {
    if (jumping) { jumping = false; clearTimeout(jumpTimer); if (state === 'on' && last) recentre(); return; }
    if (!zooming) return;
    zooming = false;
    if (state === 'on' && last) map.easeTo(camera({center: [last.coords.longitude, last.coords.latitude], duration: 300}));
  });
  // Only when the jump ended away from the latest fix (it moved meanwhile); a plain pan, no zoom.
  function recentre() {
    const c = map.getCenter?.(), p = last.coords;
    const offset = camera({}).offset;
    const projected = offset && map.project?.([p.longitude, p.latitude]), canvas = map.getContainer?.();
    const away = projected && canvas ? Math.hypot(projected.x - canvas.clientWidth / 2 - offset[0], projected.y - canvas.clientHeight / 2 - offset[1]) > 1
      : !c || Math.abs(c.lat - p.latitude) > 1e-6 || Math.abs(c.lng - p.longitude) > 1e-6;
    if (away) map.easeTo(camera({center: [p.longitude, p.latitude], duration: 300}));
  }
  map.on('rotate', () => { if (last && marker) showMarker(last.coords); });
  button.onclick = () => run('tap');
  render();
  if (start === 'follow') run('start-follow'); else if (start === 'center') run('start-center');
  return {get state() { return state; }, get position() { return last; }, tap: () => run('tap'),
    // Start following with a custom first zoom (search results).
    follow(zoomFor) { firstZoom = zoomFor; run('start-follow'); },
    disable() { firstZoom = null; run('interaction'); }};
}
